import type { ComponentType, CSSProperties } from 'react';
import {
  ToolOutlined,
  HomeOutlined,
  ThunderboltOutlined,
  CloudOutlined,
  BugOutlined,
  BgColorsOutlined,
  BlockOutlined,
  HeartOutlined,
  FireOutlined,
  ReadOutlined,
  CarOutlined,
  FilterOutlined,
  BuildOutlined,
  ToolFilled,
} from '@ant-design/icons';

type IconCmp = ComponentType<{ className?: string; style?: CSSProperties }>;

export interface ServiceIconInfo {
  Icon: IconCmp;
  color: string;
  background: string;
}

const API_ICON_MAP: Record<string, IconCmp> = {
  heart: HeartOutlined,
  fire: FireOutlined,
  tool: ToolOutlined,
  home: HomeOutlined,
  bulb: ThunderboltOutlined,
  snow: CloudOutlined,
  bug: BugOutlined,
  brush: BgColorsOutlined,
  plumbing: ToolOutlined,
  electrical: ThunderboltOutlined,
  cleaning: HomeOutlined,
};

/** Longer phrases first so "water purifier" wins over "water" / generic matches. */
const NAME_KEYWORDS: Array<{ match: string; info: ServiceIconInfo }> = [
  { match: 'water purifier', info: { Icon: FilterOutlined, color: '#0284c7', background: '#e0f2fe' } },
  { match: 'purifier', info: { Icon: FilterOutlined, color: '#0284c7', background: '#e0f2fe' } },
  { match: 'ro service', info: { Icon: FilterOutlined, color: '#0284c7', background: '#e0f2fe' } },
  { match: 'ac installation', info: { Icon: BuildOutlined, color: '#0369a1', background: '#e0f2fe' } },
  { match: 'ac install', info: { Icon: BuildOutlined, color: '#0369a1', background: '#e0f2fe' } },
  { match: 'ac repair', info: { Icon: ToolFilled, color: '#0284c7', background: '#e0f2fe' } },
  { match: 'air condition', info: { Icon: CloudOutlined, color: '#0284c7', background: '#e0f2fe' } },
  { match: 'gruha pravesh', info: { Icon: FireOutlined, color: '#b45309', background: '#fef3c7' } },
  { match: 'babysitter', info: { Icon: HeartOutlined, color: '#db2777', background: '#fce7f3' } },
  { match: 'baby', info: { Icon: HeartOutlined, color: '#db2777', background: '#fce7f3' } },
  { match: 'nanny', info: { Icon: HeartOutlined, color: '#db2777', background: '#fce7f3' } },
  { match: 'puja', info: { Icon: FireOutlined, color: '#b45309', background: '#fef3c7' } },
  { match: 'havan', info: { Icon: FireOutlined, color: '#b45309', background: '#fef3c7' } },
  { match: 'pravesh', info: { Icon: HomeOutlined, color: '#b45309', background: '#fef3c7' } },
  { match: 'gruha', info: { Icon: HomeOutlined, color: '#b45309', background: '#fef3c7' } },
  { match: 'elderly', info: { Icon: HeartOutlined, color: '#0d9488', background: '#ccfbf1' } },
  { match: 'installation', info: { Icon: BuildOutlined, color: '#0369a1', background: '#e0f2fe' } },
  { match: 'repair', info: { Icon: ToolOutlined, color: '#475569', background: '#f1f5f9' } },
  { match: 'clean', info: { Icon: HomeOutlined, color: '#00a651', background: '#dcfce7' } },
  { match: 'plumb', info: { Icon: ToolOutlined, color: '#0d9488', background: '#ccfbf1' } },
  { match: 'electr', info: { Icon: ThunderboltOutlined, color: '#ca8a04', background: '#fef9c3' } },
  { match: 'pest', info: { Icon: BugOutlined, color: '#059669', background: '#d1fae5' } },
  { match: 'paint', info: { Icon: BgColorsOutlined, color: '#7c3aed', background: '#ede9fe' } },
  { match: 'carpent', info: { Icon: BlockOutlined, color: '#b45309', background: '#fef3c7' } },
  { match: 'tutor', info: { Icon: ReadOutlined, color: '#0284c7', background: '#e0f2fe' } },
  { match: 'shift', info: { Icon: CarOutlined, color: '#64748b', background: '#f1f5f9' } },
];

const DEFAULT_INFO: ServiceIconInfo = {
  Icon: ToolOutlined,
  color: '#00a651',
  background: '#ecfdf5',
};

export type ServiceCategoryRef = {
  service_catageory_icon?: unknown;
  service_catageory_name?: string;
};

export type ServiceIconInput = {
  service_name?: string;
  name?: string;
  service_icon?: unknown;
  service_catageory?: ServiceCategoryRef;
};

export function parseServiceCategory(raw: unknown): ServiceCategoryRef | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;
  return {
    service_catageory_icon: o.service_catageory_icon,
    service_catageory_name:
      typeof o.service_catageory_name === 'string' ? o.service_catageory_name : undefined,
  };
}

export function parseServiceIcon(
  raw: unknown
): { icon?: string; color?: string; name?: string } | null {
  if (!raw) return null;
  if (typeof raw === 'object' && raw !== null) {
    const o = raw as { icon?: string; color?: string; name?: string };
    return o.icon || o.color ? o : null;
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as { icon?: string; color?: string };
      return parsed?.icon ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

function matchesAcService(name: string): boolean {
  return /\bac\b|a\/c|air condition/.test(name);
}

export function getServiceIconInfo(item: ServiceIconInput): ServiceIconInfo {
  const cat = item?.service_catageory;
  const iconData =
    parseServiceIcon(item?.service_icon) ?? parseServiceIcon(cat?.service_catageory_icon);
  const serviceName = (
    item?.service_name ?? item?.name ?? cat?.service_catageory_name ?? ''
  ).toLowerCase();

  if (iconData?.icon) {
    const key = iconData.icon.toLowerCase().trim();
    const Icon = API_ICON_MAP[key] ?? ToolOutlined;
    const color =
      iconData.color && String(iconData.color).startsWith('#')
        ? String(iconData.color)
        : DEFAULT_INFO.color;
    return { Icon, color, background: `${color}20` };
  }

  for (const { match, info } of NAME_KEYWORDS) {
    if (serviceName.includes(match)) return info;
  }

  if (matchesAcService(serviceName)) {
    return { Icon: CloudOutlined, color: '#0284c7', background: '#e0f2fe' };
  }

  return DEFAULT_INFO;
}
